"use client";

import { useTranslations } from "@i18n/provider";
import type { InsightCategorySlug } from "@lib/learner/insights/insightCategory";

const FALLBACK_STYLE = "bg-surface-sunk text-ink-muted";

/**
 * A style per category, plus one for the learner-authored insights that carry a
 * sourceType instead of a category. `satisfies` makes a new category show up
 * here as a type error rather than as an unstyled grey badge.
 */
const CATEGORY_STYLES = {
  error: "bg-coral-tint text-coral-fg",
  strength: "bg-speaking-tint text-speaking-fg",
  goal: "bg-reading-tint text-reading-fg",
  topic: "bg-writing-tint text-writing-fg",
  emotional: "bg-lavender-tint text-lavender-fg",
  preference: "bg-surface-sunk text-ink-muted",
  fluency: "bg-speaking-tint text-speaking-fg",
  vocab: "bg-reading-tint text-reading-fg",
  learner: "bg-lavender-tint text-lavender-fg",
} as const satisfies Record<InsightCategorySlug | "learner", string>;

type InsightCategoryBadgeProps = {
  category: string;
  sourceType?: string;
};

export function InsightCategoryBadge({ category, sourceType }: InsightCategoryBadgeProps) {
  const { t } = useTranslations();
  const isLearner = sourceType?.toLowerCase() === "learner";
  const key = isLearner ? "learner" : category.toLowerCase();
  // `category` arrives as an unconstrained string from the API, so the lookup
  // tolerates what the exhaustive table above does not list.
  const styles: string = (CATEGORY_STYLES as Record<string, string>)[key] ?? FALLBACK_STYLE;
  const labelKey = `profile.tutorMemory.category.${key}` as "profile.tutorMemory.category.error";
  const label = t(labelKey);
  const displayLabel = label === labelKey ? category : label;

  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-[0.08em] ${styles}`}
    >
      {displayLabel}
    </span>
  );
}
